'use client';

import ImageView from '../../../../components/ui/ImageView/ImageView';
import QuantityCounter from '../../../../components/ui/QuantityCounter/QuantityCounter';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { LotteryTradeMyGoods, TradeDesiredGoods } from '../../types';

import TextAlert from '../../../../components/ui/TextAlert/TextAlert';
import { useTradeContext } from '../../../../components/ui/TradePageClient/TradePageClient';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import { createStagingInfo } from '../../utils';
import styles from './TradeCollectionList.module.scss';

type TradeLabel = LotteryTradeMyGoods['labels'][0];

export type TradeCollectionListProps = {
  tradeCollectionGoodsList: LotteryTradeMyGoods | TradeDesiredGoods;
  showOwnedCount?: boolean;
  title?: string;
};

function TradeCollectionList({
  tradeCollectionGoodsList,
  showOwnedCount = true,
  title = '獲得した景品一覧',
}: TradeCollectionListProps) {
  const { selectedItems } = useTradeContext();

  // 特定のラベルに対してエラーメッセージを表示するかどうかを判定
  const shouldShowErrorForLabel = (label: TradeLabel): boolean => {
    // tradeableInCountが0個の場合にエラーを表示
    const tradeableInCount =
      'tradeableInCount' in label ? (label as TradeLabel & { tradeableInCount?: number }).tradeableInCount : undefined;

    return tradeableInCount === 0;
  };

  // 特定のラベルの選択済み個数を計算
  const getSelectedCountForLabel = (label: TradeLabel): number => {
    // TradeDesiredGoodsの場合はoutGoodsCountを使用（前ページで選択された数）
    const labelWithOutGoods = label as TradeLabel & { outGoodsCount?: number };
    if (labelWithOutGoods.outGoodsCount !== undefined) {
      return labelWithOutGoods.outGoodsCount;
    }

    // フォールバック：リアルタイム選択数
    let maxSelected = 0;
    label.goods?.forEach((good) => {
      const selectedCount = selectedItems.get(good.id.toString()) || 0;
      maxSelected = Math.max(maxSelected, selectedCount);
    });

    return maxSelected;
  };

  // 特定のラベル内の選択済み数量の合計を計算
  const getTotalSelectedInLabel = (label: TradeLabel): number => {
    let total = 0;

    // このラベルに属する全ての景品の選択数の合計を取得
    label.goods?.forEach((good) => {
      const selectedCount = selectedItems.get(good.id.toString()) || 0;
      total += selectedCount;
    });

    return total;
  };

  // カスタムエラーメッセージを生成
  const generateCustomErrorMessage = (label: TradeLabel & { stagingRank: string }): string => {
    const selectedCount = getSelectedCountForLabel(label);
    const requiredCount = tradeCollectionGoodsList.minTradeRequiredCount;
    const shortageCount = requiredCount - selectedCount;

    if (shortageCount > 0) {
      return `${label.stagingName}の景品をあと${shortageCount}つ交換に出す必要があります`;
    }

    return '';
  };
  // 保有状況の表示を返す関数
  const renderOwnedStatus = (good: TradeLabel['goods'][0]) => {
    if (showOwnedCount) {
      return (
        <div className={styles.ownedNumberWrapper}>
          <p className={styles.ownedNumber}>保有数：{good.acquiredCount} 個</p>
        </div>
      );
    }

    if (('isAcquired' in good && (good as { isAcquired?: boolean }).isAcquired) || good.acquiredCount >= 1) {
      return (
        <div className={styles.ownedNumberWrapper}>
          <p className={styles.ownedNumber}>獲得済み</p>
        </div>
      );
    }

    return (
      <div className={styles.ownedNumberWrapper}>
        <p className={`${styles.ownedNumber} ${utilsStyles.primaryColor}`}>未獲得</p>
      </div>
    );
  };

  return (
    <div className={`${styles.tradeCollectionList} ${utilsStyles.mtSmall}`}>
      <h5 className={utilsStyles.textBold}>{title}</h5>
      {formattedItems(tradeCollectionGoodsList.labels).map(
        (label) =>
          label.goods &&
          label.goods.length > 0 && (
            <div key={label.id} className={`${styles.itemCard}`}>
              <div>
                {label.goods.map((good) => (
                  <div key={good.id}>
                    <div className={`${styles.itemDetailWrapper} ${utilsStyles.mtXSmall}`}>
                      <div className={styles.itemImage}>
                        <ImageView alt={good.name} src={good.imageUrl} />
                      </div>
                      <div className={`${styles.itemDetail} ${utilsStyles.mlSmall}`}>
                        {/* ステージング表示 */}
                        <div className={styles.stagingRankWrapper}>{staging(label)}</div>
                        {/* 景品名 */}
                        <div className={styles.itemNameWrapper}>
                          <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{label.name}</p>
                          <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{good.name}</p>
                        </div>
                        {/* エラーメッセージ表示（該当する賞のみ） */}
                        {shouldShowErrorForLabel(label) &&
                          (() => {
                            const customMessage = generateCustomErrorMessage(label);
                            return customMessage ? (
                              <div className={`${styles.errorMessage} `}>
                                <TextAlert>{customMessage}</TextAlert>
                              </div>
                            ) : null;
                          })()}
                        <div className={styles.ownedNumberContainer}>
                          {renderOwnedStatus(good)}

                          <div className={styles.quantityCounterWrapper}>
                            <QuantityCounter
                              goodId={good.id.toString()}
                              acquiredCount={good.acquiredCount}
                              disabled={shouldShowErrorForLabel(label)}
                              tradeableInCount={
                                'tradeableInCount' in label
                                  ? (label as TradeLabel & { tradeableInCount?: number }).tradeableInCount
                                  : undefined
                              }
                              totalSelectedInLabel={getTotalSelectedInLabel(label)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ),
      )}
    </div>
  );
}

function formattedItems(items: Array<TradeLabel>) {
  const result = items.map((item) => {
    const stagingInfo = createStagingInfo(item.stagingKey);

    const formattedItem = {
      ...item,
      ...stagingInfo,
    };
    return formattedItem;
  });

  return result;
}

function getRankStyle(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1;
    case 2:
      return styles.rank2;
    default:
      return styles.defaultRankImage;
  }
}

function staging(item: TradeLabel & { stagingRank: string; stagingText: string }) {
  if (item.stagingKey) {
    return (
      <div className={`${styles.stagingRank} ${getRankStyle(Number(item.stagingRank))} `}>
        <p className={`${styles.stagingText} ${fontsStyles.montserrat}`}>
          {item.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

export default TradeCollectionList;
